import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star } from 'lucide-react';

const testimonials = [
  {
    quote: "Finally an app that doesn't feel like a spreadsheet. It actually helps me focus on what matters.",
    author: "Sarah K.",
    role: "Product Designer",
    rating: 5,
  },
  {
    quote: "The 'One Thing' approach changed how I work. I'm finishing more projects than ever before.",
    author: "Mike T.",
    role: "Startup Founder",
    rating: 5,
  },
  {
    quote: "My productivity has doubled since I started using Luma. The focus timer is game-changing.",
    author: "Jamie L.",
    role: "Software Engineer",
    rating: 5,
  },
  {
    quote: "Best focus app I've tried. Clean, minimal, and actually helps me get things done.",
    author: "Alex R.",
    role: "Content Creator",
    rating: 5,
  },
  {
    quote: "I love how it tracks my mood alongside tasks. Really helps me understand my work patterns.",
    author: "Emma W.",
    role: "Project Manager",
    rating: 5,
  },
  {
    quote: "Simple but powerful. Exactly what I needed to break out of endless to-do list anxiety.",
    author: "Chris D.",
    role: "Freelance Writer",
    rating: 5,
  },
];

export function TestimonialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 sm:py-32 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/10 to-background pointer-events-none" />
      
      <div className="relative max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Wall of Love
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join other focused professionals who've transformed their productivity.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
              className="group"
            >
              <div className="h-full bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-6 hover:border-primary/30 transition-colors duration-300">
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-foreground mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {testimonial.author.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{testimonial.author}</p>
                    <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
